import {useEffect} from "react";
import {useAppSelector} from "../../redux/hooks";
import {settingsStore} from "../../redux/settings/settingsStore";
import nativeBridge from "../../services/nativeBridge";
import {IVolumeChanged} from "../../services/eventBus/nativeApp/ToNativeAppEventTypes";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {
  subscribeToReceiveMessagesFromNativeApp
} from "../../services/eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {FromNativeAppEventTypes} from "../../services/eventBus/nativeApp/FromNativeAppEventTypes";
import {logger} from "../../services/log/logger";
import {localStorageService} from "../../services/storage/local-storage";
import {PDCStatus} from "../../redux/types/pdcStatus";
import storageKeys from "../../services/storage/storageKeys";

/**
 * Responsibility: Determine what information to send across the native bridge via subscriptions to data (e.g. Redux).
 * Ideally, all logic for determining what information to send across the native bridge and when
 * would be controlled within this component.
 * However, for practical reasons, some information is sent directly from other parts of the app.
 */
export function NativeBridge() {
  const volumes = useAppSelector(settingsStore.getVolumes);

  // Piano demo sound
  const isRightHandDemoSoundOn = useAppSelector(lessonPlayerStore.getIsDemoRightHandSoundEnabled);
  const isLeftHandDemoSoundOn = useAppSelector(lessonPlayerStore.getIsDemoLeftHandSoundEnabled);

  useEffect(function onVolumesChanged() {
    const tellNativeToChangeTheVolume = (change: Pick<IVolumeChanged, "gain" | "player">) => {
      const {gain, player} = change;
      nativeBridge.sendMessageToNative({type: "audio.volume_changed", gain, player, timestamp: Date.now()});
    }
    const pianoGain = volumes.piano;
    const metronomeGain = volumes.isMetronomeMuted ? 0 : volumes.metronome;
    const backingTrackGain = volumes.isBackingTrackMuted ? 0 : volumes.backingTrack;
    const countInVolume = volumes.countIn;
    tellNativeToChangeTheVolume({player: "piano", gain: pianoGain});
    tellNativeToChangeTheVolume({player: "metronome", gain: metronomeGain});
    tellNativeToChangeTheVolume({player: "backingTrack", gain: backingTrackGain});
    tellNativeToChangeTheVolume({player: "countIn", gain: countInVolume});
  }, [volumes]);

  useEffect(function onPianoSoundDemoChange() {
    nativeBridge.sendMessageToNative({
      type: "audio.pianoDemoMode_changed",
      isRightHand: isRightHandDemoSoundOn,
      isLeftHand: isLeftHandDemoSoundOn,
      timestamp: Date.now(),
    });
  }, [isRightHandDemoSoundOn, isLeftHandDemoSoundOn]);

  // Section: Subscribe to messages from native app
  useEffect(() => {
    // Get the current UMP status from native
    nativeBridge.sendMessageToNative({type: "UMPCheck_requested" });

    // Subscribe to future UMP status changes from native
    const unsubscribe = subscribeToReceiveMessagesFromNativeApp(onNativeApp_eventRaised);
    return () => {
      void unsubscribe();
    }
  }, []);

  // Don't render anything. This component is only for side effects.
  return null;
}

// Section: When UMP consent changes, respond appropriately
const onNativeApp_eventRaised = (e: FromNativeAppEventTypes) => {
  switch(e.type) {
    case "UMPCheck_response": // fall through
    case "UMPConsent_changed":
      logger.nativeToWebviewMessage.info({
        message: `Message from native to Webview`,
        data: [
          {key: "type", value: e.type},
          {key: "isRequired", value: e.isRequired},
          {key: "isPdcConsentApplicable", value: e.isPdcConsentApplicable},
        ]
      });
      settingsStore.setIsUMPConsentRequired(e.isRequired);
      settingsStore.setIsPdcConsentApplicable(e.isPdcConsentApplicable);
      break;
    case "getPDCStatus":
      const localValue = localStorageService.getJsonItem<PDCStatus>(storageKeys.consent_PDCStatus);
      const pdcStatus = localValue ?? "unknown";

      nativeBridge.sendMessageToNative({
        type: "PDCStatus_send",
        status: pdcStatus
      });
      break;
  }
}
